/**
 * Component for displaying and managing user bookings
 */
import React, { useState } from 'react';
import { useTeeTime } from '../hooks/useTeeTime';
import { formatDateLong } from '../utils/dateUtils';
import '../styles/UserBookings.css';

/**
 * UserBookings component for displaying and managing user's bookings
 * @returns React component
 */
const UserBookings: React.FC = () => {
  const { userBookings, getUserBookings, cancelUserBooking, loading } = useTeeTime();
  const [userEmail, setUserEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLookupForm, setShowLookupForm] = useState<boolean>(true);
  
  /**
   * Handles looking up user bookings
   * @param e Form submit event
   */
  const handleLookupBookings = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userEmail.trim()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      await getUserBookings(userEmail);
      setShowLookupForm(false);
    } catch (err) {
      console.error('Error looking up bookings:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Handles canceling a booking
   * @param bookingId ID of booking to cancel
   */
  const handleCancelBooking = async (bookingId: string) => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      setIsLoading(true);
      
      try {
        await cancelUserBooking(bookingId);
      } catch (err) {
        console.error('Error canceling booking:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  /**
   * Resets the lookup form
   */
  const handleResetLookup = () => {
    setUserEmail('');
    setShowLookupForm(true);
  };
  
  return (
    <div className="user-bookings">
      <h2>Your Bookings</h2>
      
      {showLookupForm ? (
        <form className="booking-lookup-form" onSubmit={handleLookupBookings}>
          <div className="booking-form-group">
            <label htmlFor="userEmail">Email Address:</label>
            <input
              id="userEmail"
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="Enter your email to view bookings"
              required
              disabled={isLoading}
            />
          </div>
          
          <button 
            type="submit" 
            className="lookup-button"
            disabled={isLoading || !userEmail.trim()}
          >
            {isLoading ? 'Loading...' : 'Find My Bookings'}
          </button>
        </form>
      ) : (
        <>
          <div className="user-email-display">
            <span>Bookings for: {userEmail}</span>
            <button 
              className="lookup-reset-button"
              onClick={handleResetLookup}
              disabled={isLoading}
            >
              Change Email
            </button>
          </div>
          
          {loading ? (
            <div className="bookings-loading">Loading your bookings...</div>
          ) : userBookings.length === 0 ? (
            <div className="no-bookings">
              <p>You don't have any bookings yet.</p>
            </div>
          ) : (
            <div className="bookings-list">
              {userBookings.map((booking) => {
                // Find the corresponding tee time
                const teeTime = booking.teeTimeId;
                const players = booking.players;
                
                return (
                  <div key={booking.id} className="booking-item">
                    <div className="booking-header">
                      <div className="booking-confirmation-code">
                        Confirmation: {booking.confirmationCode}
                      </div>
                      <div className="booking-date">
                        Booked on: {formatDateLong(booking.bookingDate)}
                      </div>
                    </div>
                    
                    <div className="booking-players">
                      <h4>Players ({players.length}):</h4>
                      <ul className="players-list">
                        {players.map((player) => (
                          <li key={player.id}>
                            <strong>{player.name}</strong> - {player.email}
                            {player.phone && ` - ${player.phone}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <button
                      className="cancel-booking-button"
                      onClick={() => handleCancelBooking(booking.id)}
                      disabled={isLoading}
                    >
                      Cancel Booking
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserBookings;
